import React, { useCallback, useEffect, useState } from "react";
import { handleSearchDealerByIdRequest } from "store/modules/DealerCare/thunk.store";
import { useAppDispatch } from "store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getProvidersCatalog } from "services/paymentApi";
import type { PaymentProviderCatalogDto } from "types/paymentProviderCatalog";
import * as Styled from "./styles";
import DealerPaymentDelivery from "./components/DealerPaymentDelivery";
import DealerInfo from "./components/DealerInfo";
import DealerProfile from "./components/DealerProfile";
import { useDealerPaymentData } from "./hooks/useDealerPaymentData";
import {
  DealerSearchByIdResponse,
  PaymentMethodsProps,
} from "../../store/modules/DealerCare/types.store";

function parseProvidersCatalogBody(body: unknown): PaymentProviderCatalogDto[] {
  if (Array.isArray(body)) {
    return body;
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    Array.isArray((body as { data: unknown }).data)
  ) {
    return (body as { data: PaymentProviderCatalogDto[] }).data;
  }
  return [];
}

const DealerById = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: dealerId } = useParams();
  const dispatch = useAppDispatch();

  const [dealerData, setDealerData] = useState<DealerSearchByIdResponse | null>(
    null,
  );
  const [availableProviders, setAvailableProviders] = useState<
    PaymentProviderCatalogDto[]
  >([]);

  const searchDealerData = useCallback(
    async (id: string) => {
      if (id) {
        const data = await dispatch(
          handleSearchDealerByIdRequest({
            dealerId: id,
          }),
        ).unwrap();
        setDealerData(data);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (dealerId) searchDealerData(dealerId);
  }, [searchDealerData, dealerId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await getProvidersCatalog();
        if (cancelled) return;
        setAvailableProviders(parseProvidersCatalogBody(res.data));
      } catch {
        if (!cancelled) setAvailableProviders([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const { localPaymentData, paymentMethodsSanitized, refetchLocalPayment } =
    useDealerPaymentData({
      dealerId: dealerData?.dealerInfo.id,
      paymentMethodsRaw: dealerData?.paymentMethods,
    });

  const handlePaymentDeliverySaveSuccess = useCallback(
    (updatedPaymentMethods?: PaymentMethodsProps[]) => {
      refetchLocalPayment();
      if (updatedPaymentMethods?.length) {
        setDealerData((prev) =>
          prev ? { ...prev, paymentMethods: updatedPaymentMethods } : null,
        );
      }
    },
    [refetchLocalPayment],
  );

  return (
    <Styled.Container>
      {dealerData && (
        <Styled.Content>
          <Styled.Row>
            <Button
              color="inherit"
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              {t("back")}
            </Button>
          </Styled.Row>

          <DealerInfo
            data={{
              dealerData,
              dealerTradeName: dealerData?.dealerInfo.tradeName,
              dealerEnabled: dealerData?.dealerInfo.enabled,
              dealerId: dealerData?.dealerInfo.id,
            }}
            reload={() => searchDealerData(dealerId as string)}
          />
          <DealerPaymentDelivery
            localPaymentData={localPaymentData}
            dealerId={dealerData.dealerInfo.id}
            paymentMethodsFromApi={paymentMethodsSanitized}
            onSaveSuccess={handlePaymentDeliverySaveSuccess}
            availableProviders={availableProviders}
          />
          <DealerProfile data={dealerData.dealerInfo} />
        </Styled.Content>
      )}
    </Styled.Container>
  );
};

export default DealerById;
